import type {FC, ReactNode} from 'react'
import {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {Box, Typography} from '@mui/material'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'

type DropHandler = (files: File[]) => void

/**
 * Lets ChatInput own the attachment state while the whole chat column is the
 * drop target: the composer registers its handler here, the zone below calls it.
 */
const ChatDropContext = createContext<{
  register: (handler: DropHandler | null) => void
} | null>(null)

/** Register the composer's file handler with the surrounding ChatDropZone. */
export const useChatDropTarget = (onFiles: DropHandler): void => {
  const context = useContext(ChatDropContext)
  const register = context?.register
  useEffect(() => {
    if (!register) {
      return
    }
    register(onFiles)
    return () => register(null)
  }, [register, onFiles])
}

const dragCarriesFiles = (event: React.DragEvent): boolean =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files')

/**
 * Makes a chat column accept files dropped anywhere on it, not just on the
 * composer — and, just as importantly, stops the browser from navigating away
 * to the dropped file when it lands on the message list instead.
 */
const ChatDropZone: FC<{children: ReactNode}> = ({children}) => {
  const [t] = useCustomTranslation('alpinist')
  const [isOver, setIsOver] = useState(false)
  const handlerRef = useRef<DropHandler | null>(null)
  // dragenter/dragleave also fire for every child element the pointer crosses;
  // counting them is what keeps the overlay from flickering.
  const depth = useRef(0)

  const register = useCallback((handler: DropHandler | null) => {
    handlerRef.current = handler
  }, [])
  const contextValue = useRef({register}).current

  const reset = useCallback(() => {
    depth.current = 0
    setIsOver(false)
  }, [])

  // A drag that ends anywhere but here — released over the rest of the page, or
  // off the window entirely — never reaches the zone's own handlers, and the
  // overlay would sit there until the next drag (AB#5804).
  useEffect(() => {
    window.addEventListener('drop', reset)
    window.addEventListener('dragend', reset)
    return () => {
      window.removeEventListener('drop', reset)
      window.removeEventListener('dragend', reset)
    }
  }, [reset])

  const onDragEnter = (event: React.DragEvent) => {
    if (!dragCarriesFiles(event)) {
      return
    }
    depth.current += 1
    setIsOver(true)
  }

  const onDragLeave = () => {
    // Deliberately not gated on dataTransfer: some browsers withhold the types
    // on leave, and an enter that never gets its matching decrement is exactly
    // how the zone used to get stuck.
    depth.current = Math.max(0, depth.current - 1)
    if (!depth.current) {
      setIsOver(false)
    }
  }

  const onDragOver = (event: React.DragEvent) => {
    if (dragCarriesFiles(event)) {
      // Without this the browser refuses the drop and opens the file instead.
      event.preventDefault()
    }
  }

  const onDrop = (event: React.DragEvent) => {
    reset()
    const files = Array.from(event.dataTransfer?.files ?? [])
    if (!files.length) {
      return
    }
    event.preventDefault()
    handlerRef.current?.(files)
  }

  return (
    <ChatDropContext.Provider value={contextValue}>
      <Box
        // Capture phase throughout: a child that handles the drop itself and
        // stops propagation must not be able to strand the overlay, which is
        // what the composer did before AB#5804.
        onDragEnterCapture={onDragEnter}
        onDragLeaveCapture={onDragLeave}
        onDragOverCapture={onDragOver}
        onDropCapture={onDrop}
        sx={{position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0}}
      >
        {children}
        {isOver ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 8,
              zIndex: 10,
              // The overlay must not swallow the drag events it is reacting to.
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'primary.main',
              bgcolor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(255, 255, 255, 0.85)'
                  : 'rgba(0, 0, 0, 0.75)',
            }}
          >
            <FileUploadOutlinedIcon color="primary" />
            <Typography variant="body2" color="text.secondary">
              {t('attachment_drop_here')}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </ChatDropContext.Provider>
  )
}

export default ChatDropZone
